import { useCallback, useContext, useRef, useState } from 'react';

import { DialogError } from '../../../business-error';
import { useDialog } from '../../../dialog/useDialog';
import { ToastNotificationContext } from '../../../toast-notification';
import { handleResponseError } from '../../../../../api/handleResponseError';

import { getRegenerateReportBusinessError } from './bussinessErrors';
import { RegeneratedReport, HandleRegenerateReportRequest } from './interfaces';

interface Props {
  handleSuccess: () => void;
  handleRegenerateReportRequest: HandleRegenerateReportRequest;
}

export const useRegenerateReport = ({ handleSuccess, handleRegenerateReportRequest }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const [regeneratingReport, setRegeneratingReport] = useState<RegeneratedReport>();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [isReportRegenerating, setLoading] = useState<boolean>(false);
  const [regenerateReportError, setError] = useState<DialogError>(null);

  const handleRegenerateReport = useCallback(async () => {
    try {
      if (regeneratingReport) {
        setLoading(true);
        setError(null);

        const response = await handleRegenerateReportRequest(regeneratingReport.id);

        if (response.status !== 200) {
          await handleResponseError(response);
        }

        openToast({
          message: 'Report successfully accepted for regeneration',
          severity: 'success'
        });

        setLoading(false);
        handleCloseDialog();
        handleSuccess();
      }
    } catch (e) {
      const { message, severity } = getRegenerateReportBusinessError(e);

      setError({ message, severity });
      setLoading(false);
    }
  }, [handleCloseDialog, handleRegenerateReportRequest, handleSuccess, openToast, regeneratingReport]);

  const handleOpenRegenerateDialog = useRef((report: RegeneratedReport) => {
    handleOpenDialog();
    setRegeneratingReport(report);
  });

  const handleCloseRegenerateDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    handleOpenRegenerateDialog: handleOpenRegenerateDialog.current,
    openRegenerateDialog: isDialogOpen,
    handleCloseRegenerateDialog,
    handleRegenerateReport,
    isReportRegenerating,
    regeneratingReport,
    regenerateReportError
  };
};
